import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SolutionDetails, SolutionMetadata } from '@/types/solution';
import { useSolutionForm } from '@/hooks/useSolutionForm';
import { useAIContentGeneration } from '@/hooks/useAIContentGeneration';
import { toast } from "@/hooks/use-toast";
import { useTagValidation } from '@/hooks/useTagValidation';
import { createSolutionTemplate } from '@/utils/solutionTemplate';
import ContentAssistantTab from '@/components/solution/ContentAssistantTab';
import RawContentTab from '@/components/solution/RawContentTab';
import TagValidationDialog from '@/components/TagValidationDialog';
import SuggestionInfoPopover from '@/components/SuggestionInfoPopover';

interface AddSolutionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AddSolutionDialog = ({ open, onOpenChange }: AddSolutionDialogProps) => {
  const [name, setName] = useState('');
  const [generalInfo, setGeneralInfo] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState('');
  const [jsonContent, setJsonContent] = useState('');
  const [activeTab, setActiveTab] = useState("contentAssistant");
  
  // Use a separate state to track when we should actually close the dialog
  const [shouldClose, setShouldClose] = useState(false);
  
  // Track when AI generation has completed to prevent form overrides
  const [aiContentGenerated, setAiContentGenerated] = useState(false);
  
  // Tag validation states
  const [showTagValidation, setShowTagValidation] = useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] = useState<any>(null);
  
  const { isLoading, submitSolution } = useSolutionForm({
    onSuccess: () => {
      resetForm();
      setShouldClose(true);
    }
  });

  const { validateTags, isValidating } = useTagValidation();

  const { isGenerating, generateContent } = useAIContentGeneration({
    onSuccess: (content: SolutionDetails) => {
      const contentWithMetadata = {
        metadata: {
          // Use form input for name if provided, otherwise fall back to AI
          name: name || content.metadata?.name || '',
          // Use ONLY AI-generated shortDescription, never form input
          shortDescription: content.metadata?.shortDescription || 'AI-generated description not available',
          category: categoryId,
          // Use AI-generated tags if they exist, otherwise use form tags
          tags: content.metadata?.tags || tags
        },
        ...content
      };
      
      setJsonContent(JSON.stringify(contentWithMetadata, null, 2));
      setAiContentGenerated(true);
      setActiveTab("rawContent");
    },
    onError: () => {
      setActiveTab("rawContent");
    }
  });

  // Effect to handle dialog closing after successful submission
  useEffect(() => {
    if (shouldClose) {
      onOpenChange(false);
      setShouldClose(false);
    }
  }, [shouldClose, onOpenChange]);

  // Initialize JSON template when dialog opens with centralized template
  useEffect(() => {
    if (open) {
      const templateDetails = createSolutionTemplate();
      setJsonContent(JSON.stringify(templateDetails, null, 2));
      setActiveTab("contentAssistant");
    }
  }, [open]);

  // Update JSON metadata when basic info changes
  useEffect(() => {
    if (aiContentGenerated) {
      // Don't override AI-generated content immediately after generation
      setAiContentGenerated(false);
      return;
    }
    
    if (open && jsonContent && (name || generalInfo || categoryId || tags.length > 0 || imageUrl)) {
      try {
        const parsedContent = JSON.parse(jsonContent);
        
        // Preserve existing tags if form tags are empty
        const existingTags = parsedContent.metadata?.tags || [];
        const tagsToUse = tags.length > 0 ? tags : existingTags;
        
        const updatedMetadata: SolutionMetadata = {
          // Form input has priority for name
          name: name || parsedContent.metadata?.name || "",
          // Preserve AI-generated shortDescription
          shortDescription: parsedContent.metadata?.shortDescription || "",
          category: categoryId || "",
          // Use form tags if provided, otherwise preserve existing tags
          tags: tagsToUse,
          imageUrl: imageUrl || parsedContent.metadata?.imageUrl || ""
        };
        
        const updatedContent = {
          ...parsedContent,
          metadata: updatedMetadata
        };
        
        setJsonContent(JSON.stringify(updatedContent, null, 2));
      } catch (error) {
        console.error('JSON parsing error during metadata update');
      }
    }
  }, [name, generalInfo, categoryId, tags, imageUrl, open, aiContentGenerated]);

  // Handle cancel button with confirmation if form has data
  const handleCancelClick = () => {
    const hasData = name || generalInfo || categoryId || tags.length > 0 || imageUrl ||
      (jsonContent && jsonContent !== '{}');
      
    if (!hasData || window.confirm('Are you sure you want to discard your changes?')) {
      resetForm();
      onOpenChange(false);
    }
  };

  const resetForm = () => {
    setName('');
    setGeneralInfo('');
    setCategoryId('');
    setTags([]);
    setImageUrl('');
    setJsonContent('');
    setActiveTab("contentAssistant");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let parsedDetails: SolutionDetails | null = null;
    
    try {
      parsedDetails = jsonContent ? JSON.parse(jsonContent) : null;
    } catch (err) {
      console.error('Invalid JSON:', err);
      parsedDetails = null;
    }
    
    // Extract tags directly from JSON metadata (Raw Content tab)
    const extractedTagsFromJson = Array.isArray(parsedDetails?.metadata?.tags)
      ? (parsedDetails!.metadata!.tags as string[])
      : [];

    const submissionData = {
      name,
      shortDescription: parsedDetails?.metadata?.shortDescription || 'No description available',
      categoryId,
      tags,
      imageUrl,
      details: parsedDetails
    };
    
    // Validate tags before submission
    try {
      const validationResult = await validateTags(extractedTagsFromJson);
      
      if (validationResult.hasNewTags) {
        // Store submission data and show validation dialog
        setPendingSubmissionData({
          submissionData,
          validationResult
        });
        setShowTagValidation(true);
        return;
      }
      
      // No new tags, proceed with normal submission
      await submitSolution(submissionData);
    } catch (error) {
      console.error('Tag validation error:', error);
      // Inform user and proceed with submission anyway
      toast({
        title: "Tag validation unavailable",
        description: "Proceeding with submission. Admins will validate tags during review.",
      });
      await submitSolution(submissionData);
    }
  };
  
  const handleTagValidationConfirm = async () => {
    if (pendingSubmissionData) {
      setShowTagValidation(false);
      await submitSolution(pendingSubmissionData.submissionData);
      setPendingSubmissionData(null);
    }
  };
  
  const handleTagValidationCancel = () => {
    setShowTagValidation(false);
    setPendingSubmissionData(null);
  };

  const handleGenerateContent = async () => {
    await generateContent(name, generalInfo);
  };

  const handleNext = () => {
    setActiveTab("rawContent");
  };

  const isFormValid = Boolean(name);

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      // Only allow closing via the onOpenChange if there's no data
      // or the user confirms they want to discard changes
      if (open && !newOpen) {
        handleCancelClick();
      } else {
        onOpenChange(newOpen);
      }
    }}>
      <DialogContent className="w-[95%] max-w-4xl max-h-[80vh] sm:max-h-[90vh] flex flex-col p-0 overflow-hidden sm:w-[90%] sm:max-w-6xl">
        <SuggestionInfoPopover className="absolute right-14 top-4" />
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Add New AI Solution</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="sticky top-0 z-10 bg-background pt-1">
                <TabsList className="w-full">
                  <TabsTrigger value="contentAssistant" className="flex-1">Content Assistant</TabsTrigger>
                  <TabsTrigger value="rawContent" className="flex-1">Raw Content</TabsTrigger>
                </TabsList>
              </div>
              
              {/* Content Assistant Tab */}
              <TabsContent value="contentAssistant">
                <ContentAssistantTab
                  name={name}
                  setName={setName}
                  generalInfo={generalInfo}
                  setGeneralInfo={setGeneralInfo}
                  imageUrl={imageUrl}
                  setImageUrl={setImageUrl}
                />
              </TabsContent>
              
              {/* Raw Content Tab */}
              <TabsContent value="rawContent">
                <RawContentTab
                  jsonContent={jsonContent}
                  setJsonContent={setJsonContent}
                  solutionName={name}
                  solutionShortDescription={(() => {
                    try {
                      const parsed = JSON.parse(jsonContent || '{}');
                      return parsed.metadata?.shortDescription || 'No description available';
                    } catch {
                      return 'No description available';
                    }
                  })()}
                  solutionTags={tags}
                />
              </TabsContent>
            </Tabs>
          </div>
          
          <div className="sticky bottom-0 bg-background py-4 border-t">
            <div className="flex justify-start sm:justify-end px-4 sm:px-6 py-2 overflow-x-auto">
              <div className="flex items-center gap-3 min-w-max">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCancelClick}
                  disabled={isLoading || isGenerating}
                >
                  Cancel
                </Button>

                {activeTab === "contentAssistant" && (
                  <>
                    <Button 
                      type="button"
                      onClick={handleGenerateContent}
                      disabled={isLoading || isGenerating || !isFormValid}
                      variant="default"
                    >
                      {isGenerating ? "Generating..." : "Generate Content"}
                    </Button>

                    <Button 
                      type="button"
                      onClick={handleNext}
                      disabled={isLoading || isGenerating || !isFormValid}
                      variant="secondary"
                    >
                      Next: Raw Content
                    </Button>
                  </>
                )}

                {activeTab === "rawContent" && (
                  <Button 
                    type="submit"
                    disabled={isLoading || isGenerating || isValidating || !isFormValid}
                  >
                    {(isLoading || isValidating) ? "Processing..." : "Submit"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
      
      {/* Tag Validation Dialog */}
      {pendingSubmissionData && (
        <TagValidationDialog
          open={showTagValidation}
          onOpenChange={setShowTagValidation}
          newTags={pendingSubmissionData.validationResult?.newTags || []}
          existingTags={pendingSubmissionData.validationResult?.existingTags || []}
          onConfirm={handleTagValidationConfirm}
          onCancel={handleTagValidationCancel}
          context="submission"
          isLoading={isLoading}
        />
      )}
    </Dialog>
  );
};

export default AddSolutionDialog;
